import { StoreInterface } from '../../store/types';

// FoodAmigos' platform Mixpanel project is the fallback for every white-label partner shop whose
// provider has no key of its own (resolveMixpanelProject). Before the fallback existed those shops ran
// no Mixpanel at all, so on the platform project nothing is tracked or stored until the customer accepts
// cookies: the SDK starts opted out with persistence off (no mp_ cookie), an explicit accept opts it in,
// and a decline or a later withdrawal opts it out again. A provider's own project is not touched.

type CookiesConsent = StoreInterface['appStatus']['cookiesConsent'];

/** Extra init options for the platform fallback. */
export const PLATFORM_PROJECT_CONSENT_CONFIG = {
  opt_out_tracking_by_default: true,
  disable_persistence: true,
};

/** The subset of the Mixpanel SDK this needs, so tests can hand in a fake. */
export interface MixpanelConsentClient {
  __loaded?: boolean;
  get_config: (name: string) => unknown;
  set_config: (config: { disable_persistence: boolean }) => void;
  has_opted_in_tracking: () => boolean;
  opt_in_tracking: (options: { track: () => void }) => void;
  opt_out_tracking: (options: { delete_user: boolean }) => void;
}

/**
 * Whether this Mixpanel instance is the consent-gated platform one. Read from the instance's own config
 * rather than re-resolved from the company: init() only takes effect once per page, so the instance
 * keeps tracking into the project it was first initialised with.
 */
export function isConsentGated(mp: Pick<MixpanelConsentClient, '__loaded' | 'get_config'>): boolean {
  return mp?.['__loaded'] === true && mp.get_config('opt_out_tracking_by_default') === true;
}

/**
 * Brings a consent-gated instance in line with the cookie-banner answer. 'true' turns persistence on
 * and opts in; anything else (declined, or not answered yet) keeps it, or puts it back, opted out with
 * nothing stored. The SDK remembers an opt-in in localStorage, which can outlive the consent cookie, so
 * a remembered opt-in without a current 'true' is undone too. Idempotent.
 *
 * Returns true when this call opted the instance in, so the caller can re-apply what the SDK ignored
 * while it was opted out (set_group is a no-op then).
 */
export function applyPlatformConsent(mp: MixpanelConsentClient, consent: CookiesConsent): boolean {
  if (consent === 'true') {
    mp.set_config({ disable_persistence: false });

    if (mp.has_opted_in_tracking()) {
      return false;
    }

    // No `$opt_in` event: it is not in the tracking plan.
    mp.opt_in_tracking({ track: () => undefined });

    return true;
  }

  if (mp.has_opted_in_tracking()) {
    // Clears and disables the SDK's persistence (the mp_ cookie) and its send queue. The people
    // profile is kept: withdrawing consent stops tracking, it is not an erasure request.
    mp.opt_out_tracking({ delete_user: false });
  }

  mp.set_config({ disable_persistence: true });

  return false;
}

/**
 * Whether the browser's Mixpanel identity may be handed on (the backend ties order_placed to it and
 * puts it in the Stripe metadata): on a provider's own project as before, on the platform project only
 * once the customer opted in.
 */
export function mayShareMixpanelIdentity(mp: MixpanelConsentClient): boolean {
  return !isConsentGated(mp) || mp.has_opted_in_tracking();
}
